
import { useState, useEffect, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { toast } from "@/hooks/use-toast";
import { geocodeAddress } from "@/services/geocodingService";

interface GoogleMapPreviewProps {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  initialLatitude?: number | null;
  initialLongitude?: number | null;
  onCoordinatesChange?: (lat: number, lng: number) => void;
}

const containerStyle = {
  width: '100%',
  height: '200px',
  borderRadius: '0.375rem'
};

const defaultCenter = {
  lat: 39.8283, // Center of US
  lng: -98.5795
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

// Custom gold pin marker component
const GoldPin = () => (
  <div className="relative">
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.2 0 0 7.2 0 16C0 24 16 40 16 40S32 24 32 16C32 7.2 24.8 0 16 0Z" fill="#ffd700"/>
      <circle cx="16" cy="16" r="8" fill="#080c14"/>
      <text x="16" y="20" textAnchor="middle" fill="#ffd700" fontSize="8" fontWeight="bold">$</text>
    </svg>
  </div>
);

export const GoogleMapPreview = ({ 
  address, 
  city, 
  state, 
  zipCode,
  initialLatitude,
  initialLongitude,
  onCoordinatesChange
}: GoogleMapPreviewProps) => {
  const [center, setCenter] = useState(defaultCenter);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);

  // Use initial coordinates if available
  useEffect(() => {
    if (initialLatitude != null && initialLongitude != null) {
      setCenter({ lat: initialLatitude, lng: initialLongitude });
      setHasAddress(true);
    }
  }, [initialLatitude, initialLongitude]);

  // Determine whether we have enough address signal to attempt geocoding.
  // Accept several partial combinations so the map still renders while the
  // user is typing the address manually.
  const hasGeocodeSignal = (
    (!!address && !!city && !!state) ||
    (!!address && !!zipCode) ||
    (!!city && !!state && !!zipCode) ||
    (!!city && !!state)
  );

  // Geocode the address to get coordinates
  const geocodeLocationAddress = useCallback(async () => {
    if (!hasGeocodeSignal) return;

    setIsLoading(true);
    setHasAddress(true);

    try {
      const result = await geocodeAddress(address, city, state, zipCode);

      if (result) {
        setCenter({ lat: result.lat, lng: result.lng });
        // Notify parent component of new coordinates
        onCoordinatesChange?.(result.lat, result.lng);
      } else {
        console.error("Geocoding failed for:", `${address}, ${city}, ${state} ${zipCode}`);
        toast({
          title: "Location not found",
          description: "Could not find the location on the map",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error geocoding address:", error);
      toast({
        title: "Error loading map",
        description: "Failed to load the map for this location",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [address, city, state, zipCode, onCoordinatesChange, hasGeocodeSignal]);

  useEffect(() => {
    if (hasGeocodeSignal) {
      // Skip geocoding if we already have coordinates from props
      if (initialLatitude != null && initialLongitude != null) {
        return;
      }

      // Debounce the geocoding request to avoid making too many API calls
      const timer = setTimeout(() => {
        geocodeLocationAddress();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [hasGeocodeSignal, geocodeLocationAddress, initialLatitude, initialLongitude]);


  if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID) {
    return (
      <div className="border border-border rounded-md h-[200px] bg-muted flex items-center justify-center">
        <p className="text-destructive">Google Maps configuration missing</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-md z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godyspo-gold"></div>
        </div>
      )}
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          mapId={GOOGLE_MAPS_MAP_ID}
          style={containerStyle}
          center={center}
          zoom={hasAddress ? 15 : 4}
          disableDefaultUI={true}
          gestureHandling="greedy"
        >
          {hasAddress && (
            <AdvancedMarker position={center}>
              <GoldPin />
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};
